import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@carbon/react";
import { currencyFormatter } from "../../utils";
import { DeliveryInterface, OfferResponse } from "../../types";

export interface DeliveryOffersTableProps {
  delivery: DeliveryInterface;
  selectedOffer?: OfferResponse;
  selectedOffers: OfferResponse[];
  total: number;
}

/**
 * The awarded-materials/pricing table on the delivery detail. Two mutually-exclusive
 * shapes preserved from the pre-Carbon screen:
 *  - single-line: `order` + one `selectedOffer` (when no multi-order offers are selected),
 *  - multi-line: the `selectedOffers` list of a joint/multiple order, with a summed total.
 * Read-only display for both roles.
 */
const DeliveryOffersTable = ({
  delivery,
  selectedOffer,
  selectedOffers,
  total,
}: DeliveryOffersTableProps) => {
  const { t } = useTranslation();

  const headers = [
    t("materials.title"),
    t("tenders.quantity"),
    t("tenders.price"),
    t("tenders.total"),
  ];

  const showSingle =
    Boolean(delivery.order) && Boolean(selectedOffer) && selectedOffers.length === 0;

  if (!showSingle && selectedOffers.length === 0) return null;

  const renderHead = () => (
    <TableHead>
      <TableRow>
        {headers.map((header, index) => (
          <TableHeader key={header} id={`offers-col-${index}`}>
            {header}
          </TableHeader>
        ))}
      </TableRow>
    </TableHead>
  );

  if (showSingle) {
    const singleTotal =
      (delivery.order?.quantity || 0) * (selectedOffer?.price || 0);
    return (
      <div className="delivery-panel__table">
        <Table size="lg" useZebraStyles>
          {renderHead()}
          <TableBody>
            <TableRow>
              <TableCell>
                {delivery.order?.material?.globalMaterialName}
              </TableCell>
              <TableCell>
                {delivery.order?.quantity} {delivery.order?.measurementUnit}
              </TableCell>
              <TableCell>{currencyFormatter(selectedOffer?.price)}</TableCell>
              <TableCell>
                {currencyFormatter(singleTotal, delivery.order?.currency)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("tenders.total")}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell>
                {currencyFormatter(singleTotal, delivery.order?.currency)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="delivery-panel__table">
      <Table size="lg" useZebraStyles>
        {renderHead()}
        <TableBody>
          {selectedOffers.map((offer) => (
            <TableRow key={offer.id}>
              <TableCell>
                {offer?.order?.material?.globalMaterialName}
              </TableCell>
              <TableCell>
                {offer?.order?.quantity} {offer?.order?.measurementUnit}
              </TableCell>
              <TableCell>{currencyFormatter(offer?.price)}</TableCell>
              <TableCell>
                {currencyFormatter(
                  (offer?.order?.quantity || 0) * offer?.price,
                  offer?.currency
                )}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>{t("tenders.total")}</TableCell>
            <TableCell />
            <TableCell />
            <TableCell>
              {currencyFormatter(total, delivery.order?.currency)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default DeliveryOffersTable;
